import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, upload } from "../api/client";

// Data layer for the Table of Contents review.
//
// The server recomputes the checks on every read — there is no artifact to go
// stale — and returns them merged with the reviewer's own layer. Everything
// the reviewer does is a section of that layer, saved back with PATCH; each
// section is replaced wholesale, so two tabs editing different sections don't
// clobber each other.

/** The per-cell review columns, in table order. */
export const CELL_COLUMNS = [
  "product_id",
  "chapter_name",
  "hierarchy",
  "audio_track",
  "timestamp",
  "accuracy",
] as const;
export type CellColumn = (typeof CELL_COLUMNS)[number];

export const COLUMN_LABELS: Record<CellColumn, string> = {
  product_id: "PRODUCT ID",
  chapter_name: "Chapter Name",
  hierarchy: "Hierarchy Level #",
  audio_track: "Audio Track",
  timestamp: "Timestamp Format",
  accuracy: "Timestamp Accuracy",
};

/**
 * The sheet column each review column is addressed by. Timestamp accuracy is
 * judged on the Timestamp cell — it has no column of its own in the sheet.
 */
export const COLUMN_LETTERS: Record<CellColumn, string> = {
  product_id: "A",
  chapter_name: "B",
  hierarchy: "C",
  audio_track: "D",
  timestamp: "E",
  accuracy: "E",
};

export function cellAddress(col: CellColumn, row: TocRow): string {
  return `${COLUMN_LETTERS[col]}${row.sheet_row}`;
}

/**
 * Cell addresses for the report, collapsing a fully-flagged column to "All".
 * A producer reading "All" acts on the column; forty addresses tell them
 * nothing they can use. Mirrors `modules/toc/report.py`.
 */
export function formatCells(refs: string[], nRows: number): string {
  if (!refs.length) return "—";
  const columns = new Set(refs.map((r) => r[0]));
  if (columns.size === 1 && nRows && refs.length >= nRows) return "All";
  return refs.join(", ");
}

export interface TocRow {
  index: number;
  sheet_row: number;
  product_id: string;
  chapter_name: string;
  hierarchy: string;
  audio_track: string;
  timestamp: string;
  first_in_file: boolean;
  /** The delivered file the ear test plays for this row, "" if none. */
  audio_file: string;
  /** The name that file goes by (a corrected delivery's stored "(v2)" name
      left out), for showing it. */
  audio_name?: string;
  /** How that file was found: an exact name match, a positional guess (the
      ToC uses a different naming convention), or nothing. */
  audio_match: "exact" | "assumed" | "none";
  /** Physical manuscript page the tool matched this chapter's heading to, so the
      cell can offer a "view the page" glance-check. null when it matched nothing
      or the section couldn't be placed on a page. */
  manuscript_page: number | null;
}

export interface TocFlag {
  row_index: number;
  cell_ref: string;
  reason: string;
}

export interface TocMetric {
  id: string;
  label: string;
  column: string;
  /** null ⇒ the tool doesn't score this metric; a person decides. */
  verdict: boolean | null;
  comment: string;
  flags: TocFlag[];
}

export interface ExpectedItem {
  name: string;
  hierarchy: string;
  evidence: string;
  /** Physical manuscript page of this section's heading, 0 when unplaced. */
  page?: number;
  note: string;
}

export interface ExpectedToc {
  items: ExpectedItem[];
  number_style: string;
  has_printed_toc: boolean;
  duplicates: string[];
  concerns: string[];
  extracted_at?: string;
  n_pages?: number;
}

/** The reviewer's layer. Keys are column then row index, both as strings. */
export interface TocReviewState {
  marks?: Record<string, Record<string, boolean>>;
  ticks?: Record<string, Record<string, boolean>>;
  comments?: Record<string, Record<string, string>>;
  heard?: Record<string, boolean>;
  timestamps?: Record<string, string>;
  report_comments?: Record<string, string>;
}

/** The manuscript-vs-ToC alignment. Per-cell near-miss / hierarchy flags ride
    in `flags`; this carries the list-level missing/extra findings. */
export interface TocComparison {
  /** Chapters the manuscript has but the ToC doesn't — possible drops. `page`
      is the manuscript page of the dropped heading (0 when unplaced). */
  missing: { name: string; hierarchy: string; evidence?: string; page?: number }[];
  /** ToC rows with no manuscript counterpart — usually production bookends. */
  extra: { row_index: number; name: string }[];
  matched: number;
}

/** The endpoint that renders one manuscript page to a PNG (auth'd — load it
    through `fetchBlobUrl`, not a bare `<img src>`). */
export function manuscriptPageUrl(projectId: string, page: number): string {
  return `/api/modules/toc/projects/${projectId}/manuscript-page/${page}`;
}

export interface TocPayload {
  filename: string;
  uploaded_at: string | null;
  product_id: string;
  sheet_name: string;
  fatal: string | null;
  audio_available: boolean;
  columns: CellColumn[];
  auto_columns: CellColumn[];
  rows: TocRow[];
  metrics: TocMetric[];
  flags: Record<string, Record<string, string>>;
  review: TocReviewState;
  expected: ExpectedToc | null;
  comparison: TocComparison | null;
  /** Set on the upload response when a manuscript read was auto-started. */
  extraction_job_id?: string | null;
}

// --- the review model --------------------------------------------------------
//
// Every tested cell can be flagged. The tool's own opinion is the default; the
// reviewer only clicks to disagree, which is what keeps this an exception
// review rather than a full read.

/** Did the tool itself raise a flag on this cell? */
export function toolFlag(p: TocPayload, col: CellColumn, row: TocRow): boolean {
  return p.flags[col]?.[String(row.index)] !== undefined;
}

export function toolReason(p: TocPayload, col: CellColumn, row: TocRow): string {
  return p.flags[col]?.[String(row.index)] ?? "";
}

/** The effective flag: the reviewer's override if there is one, else the tool's. */
export function flagged(p: TocPayload, col: CellColumn, row: TocRow): boolean {
  const mark = p.review.marks?.[col]?.[String(row.index)];
  return mark === undefined ? toolFlag(p, col, row) : mark;
}

export function raisedByYou(p: TocPayload, col: CellColumn, row: TocRow): boolean {
  return flagged(p, col, row) && !toolFlag(p, col, row);
}

export function clearedByYou(p: TocPayload, col: CellColumn, row: TocRow): boolean {
  return !flagged(p, col, row) && toolFlag(p, col, row);
}

/**
 * A first-in-file 00:00:00.000 is where that file necessarily starts, so it is
 * assumed right and never put in front of the reviewer. (Still flaggable by
 * hand if the ear says otherwise.)
 */
export function assumedCorrect(_p: TocPayload, col: CellColumn, row: TocRow): boolean {
  return col === "accuracy" && row.first_in_file && row.timestamp === "00:00:00.000";
}

/** A cell wants the reviewer's eye when the tool can't settle it on its own. */
export function reviewable(p: TocPayload, col: CellColumn, row: TocRow): boolean {
  if (p.auto_columns.includes(col)) return false; // the tool decides these definitively
  if (flagged(p, col, row) || clearedByYou(p, col, row)) return false; // surfaced / actioned
  if (assumedCorrect(p, col, row)) return false;
  return true;
}

export function ticked(p: TocPayload, col: CellColumn, row: TocRow): boolean {
  return !!p.review.ticks?.[col]?.[String(row.index)];
}

export function needsReview(p: TocPayload, col: CellColumn, row: TocRow): boolean {
  return reviewable(p, col, row) && !ticked(p, col, row);
}

/**
 * The tool has resolved this cell (an auto-checked pass, or an assumed-correct
 * first-in-file zero), so nothing is being asked of the reviewer and it reads
 * as already ticked off.
 */
export function settled(p: TocPayload, col: CellColumn, row: TocRow): boolean {
  return !flagged(p, col, row) && !reviewable(p, col, row);
}

export function cellNote(p: TocPayload, col: CellColumn, row: TocRow): string {
  return p.review.comments?.[col]?.[String(row.index)] ?? "";
}

/** Finish the tool's sentence so the reviewer's note reads as the next one. */
export function endSentence(text: string): string {
  const s = (text ?? "").trim();
  if (!s) return "";
  return /[.!?]["'”’)]?$/.test(s) ? s : `${s}.`;
}

/** Every flag still standing, in table order — this is what the report shows. */
export function openFlags(p: TocPayload): { col: CellColumn; row: TocRow }[] {
  const out: { col: CellColumn; row: TocRow }[] = [];
  for (const row of p.rows) {
    for (const col of p.columns) {
      if (flagged(p, col, row)) out.push({ col, row });
    }
  }
  return out;
}

export function reviewStats(p: TocPayload) {
  let toReview = 0;
  let flags = 0;
  let heard = 0;
  let hearable = 0;
  for (const row of p.rows) {
    for (const col of p.columns) {
      if (flagged(p, col, row)) flags += 1;
      if (needsReview(p, col, row)) toReview += 1;
    }
    if (row.audio_track) {
      hearable += 1;
      if (p.review.heard?.[String(row.index)]) heard += 1;
    }
  }
  return { toReview, flags, heard, hearable };
}

// --- hook --------------------------------------------------------------------

export function useToc(projectId: string) {
  const qc = useQueryClient();
  const key = ["toc", projectId];

  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: async () => {
      try {
        return await api<TocPayload>(`/api/modules/toc/projects/${projectId}/toc`);
      } catch (e) {
        // No ToC uploaded yet is the normal empty state, not a failure.
        if ((e as { status?: number }).status === 404) return null;
        throw e;
      }
    },
  });

  const save = useMutation({
    mutationFn: (patch: TocReviewState) =>
      api<TocPayload>(`/api/modules/toc/projects/${projectId}/toc/review`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: (fresh) => qc.setQueryData(key, fresh),
  });

  const uploadToc = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return upload<TocPayload>(`/api/modules/toc/projects/${projectId}/toc`, form);
    },
    onSuccess: (fresh) => {
      qc.setQueryData(key, fresh);
      qc.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });

  const remove = useMutation({
    mutationFn: () =>
      api(`/api/modules/toc/projects/${projectId}/toc`, { method: "DELETE" }),
    onSuccess: () => {
      qc.setQueryData(key, null);
      qc.invalidateQueries({ queryKey: ["project", projectId] });
    },
  });

  /**
   * Apply a change to one section of the review. The whole section is sent, so
   * the server never has to merge per-cell edits — and the local cache is
   * updated first so flagging a cell feels instant.
   */
  function edit(mutate: (r: TocReviewState) => TocReviewState) {
    const current = qc.getQueryData<TocPayload | null>(key);
    if (!current) return;
    const next = mutate(current.review ?? {});
    qc.setQueryData<TocPayload>(key, { ...current, review: next });
    save.mutate(next);
  }

  // Re-derive from the server — used after the manuscript read finishes, so
  // the comparison the extraction produced shows up without a manual reload.
  const refresh = () => qc.invalidateQueries({ queryKey: key });

  return { toc: data ?? null, isLoading, error, edit, uploadToc, remove, refresh, saving: save.isPending };
}
